import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { EmployerGuideContext } from '@/shared/api/onboarding';
import styles from './SetupGuide.module.css';

const chf = (n: number | null | undefined): string =>
  n == null ? '—' : `CHF ${Math.round(n).toLocaleString('de-CH')}`;
const pct = (n: number | null | undefined): string => (n == null ? '—' : `${n}%`);

/** Personalized Ausgleichskasse card — the employer's own fund, contacts, and registration status. */
export function CantonInfoCard({ ctx }: { ctx: EmployerGuideContext }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ak = ctx.ausgleichskasse;

  if (!ctx.canton || !ak) {
    return (
      <div className={styles.warnCard}>
        {t('guide.employer.register.noCanton')}{' '}
        <button className={styles.docLink} onClick={() => navigate('/settings/household')}>
          {t('guide.employer.register.setCanton')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.infoCard}>
      <div className={styles.infoCardTitle}>
        <i className="fa-solid fa-location-dot" aria-hidden="true" />
        {t('guide.employer.register.yourKasse', { canton: ctx.canton })}
      </div>
      <p className={styles.kasseName}>{ak.name}</p>
      <div className={styles.kasseMeta}>
        {ak.phone && (
          <span>
            <i className="fa-solid fa-phone" aria-hidden="true" /> {ak.phone}
          </span>
        )}
        {ak.website && (
          <a href={ak.website} target="_blank" rel="noopener noreferrer">
            <i className="fa-solid fa-globe" aria-hidden="true" /> {ak.website.replace(/^https?:\/\//, '')}
          </a>
        )}
      </div>
      {ctx.registration.isRegistered ? (
        <span className={styles.autoPill}>
          <i className="fa-solid fa-check" aria-hidden="true" />
          {ctx.registration.compensationOfficeId
            ? t('guide.employer.register.registeredAs', { id: ctx.registration.compensationOfficeId })
            : t('guide.employer.register.registeredShort')}
        </span>
      ) : null}
    </div>
  );
}

/** Contribution rates + thresholds table for the employer's canton and year. */
export function RatesCard({ ctx }: { ctx: EmployerGuideContext }) {
  const { t } = useTranslation();
  const r = ctx.rates;
  const th = ctx.thresholds;

  return (
    <div className={styles.tableScroll}>
      <table className={styles.ratesTable}>
        <thead>
          <tr>
            <th>{t('guide.employer.rates.contribution')}</th>
            <th>{t('guide.employer.rates.employerShare')}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{t('guide.employer.rates.ahvIvEo')}</td>
            <td>{pct(r?.ahvIvEo)}</td>
          </tr>
          <tr>
            <td>{t('guide.employer.rates.alv')}</td>
            <td>{pct(r?.alv)}</td>
          </tr>
          <tr>
            <td>{t('guide.employer.rates.fak', { canton: ctx.canton ?? '' })}</td>
            <td>{pct(r?.fak)}</td>
          </tr>
          <tr>
            <td>{t('guide.employer.rates.flatTax')}</td>
            <td>{pct(th.flatTaxRate)}</td>
          </tr>
          <tr>
            <td>{t('guide.employer.rates.bvgEntry')}</td>
            <td>{chf(th.bvgEntry)}</td>
          </tr>
          <tr>
            <td>{t('guide.employer.rates.sackgeld')}</td>
            <td>{chf(th.sackgeldAmount)}</td>
          </tr>
        </tbody>
      </table>
      {r?.fakNotes ? <p className={styles.stepText} style={{ marginTop: 'var(--space-2)' }}>{r.fakNotes}</p> : null}
    </div>
  );
}

/** Live VAV/VAVplus eligibility for the employer's current payroll. */
export function VavEligibilityCard({ ctx }: { ctx: EmployerGuideContext }) {
  const { t } = useTranslation();
  const e = ctx.vavEligibility;

  if (ctx.vavRestrictedForFrenchCrossBorder) {
    return (
      <div className={styles.warnCard}>
        {t('guide.employer.procedure.crossBorderWarn', { canton: ctx.canton ?? '' })}
      </div>
    );
  }
  if (!e) return null;

  return (
    <div className={styles.infoCard}>
      <div className={styles.infoCardTitle}>
        <i className={`fa-solid ${e.qualifiesNow ? 'fa-circle-check' : 'fa-circle-exclamation'}`} aria-hidden="true" />
        {e.qualifiesNow
          ? t('guide.employer.procedure.eligible')
          : t('guide.employer.procedure.notEligible')}
      </div>
      <p className={styles.stepText}>
        {t('guide.employer.procedure.payroll', {
          amount: chf(e.totalPayroll),
          max: chf(ctx.thresholds.vavTotalPayroll),
        })}
      </p>
    </div>
  );
}

/** Known filing deadlines; surfaces the employer's stored next deadlines when present. */
export function DeadlinesCard({ ctx }: { ctx: EmployerGuideContext }) {
  const { t } = useTranslation();
  const d = ctx.nextDeadlines;

  // Only render entries that carry a real, sensible date. Guards against a null or epoch
  // value (e.g. withholding tax under the simplified procedure) surfacing as "1.1.1970".
  const entries = Object.entries(d ?? {}).filter(([, v]) => {
    if (!v) return false;
    const dt = new Date(v);
    return !isNaN(dt.getTime()) && dt.getFullYear() > 1971;
  });
  if (entries.length === 0) return null;

  const fmt = (v: string): string => new Date(v).toLocaleDateString('de-CH');

  return (
    <div className={styles.infoCard}>
      <div className={styles.infoCardTitle}>
        <i className="fa-solid fa-calendar-day" aria-hidden="true" />
        {t('guide.employer.deadlines.yourNext')}
      </div>
      <div className={styles.tableScroll}>
        <table className={styles.ratesTable}>
          <tbody>
            {entries.map(([key, val]) => (
              <tr key={key}>
                <td>{t(`guide.employer.deadlines.${key}`, key)}</td>
                <td>{fmt(val)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export interface DocItem {
  id: string;
  icon: string;
  titleKey: string;
  bodyKey: string;
  /** Internal route for a real, built document feature; omit for a "coming soon" placeholder. */
  href?: string;
  ctaKey?: string;
}

/** Renders a grid of document cards — real ones link out, placeholders show "coming soon". */
export function DocumentList({ items }: { items: DocItem[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className={styles.docGrid}>
      {items.map((item) => (
        <div key={item.id} className={styles.docCard}>
          <div className={styles.docTitle}>
            <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
            {t(item.titleKey)}
          </div>
          <p className={styles.docText}>{t(item.bodyKey)}</p>
          {item.href ? (
            <button className={styles.docLink} onClick={() => navigate(item.href!)}>
              {t(item.ctaKey ?? 'guide.ui.open')}
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          ) : (
            <span className={styles.soonBadge}>{t('guide.ui.comingSoon')}</span>
          )}
        </div>
      ))}
    </div>
  );
}
